import React from 'react';
import { InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import { components } from 'react-select';

const getSummaryNoun = (placeholderText = '') => {
  const cleaned = String(placeholderText || '').trim();
  if (!cleaned) return 'selected';

  const withoutAll = cleaned.replace(/^all\s+/i, '').trim();
  return withoutAll || 'selected';
};

const singularize = (word = '') => {
  if (word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.endsWith('s')) return word.slice(0, -1);
  return word;
};

const buildMultiSelectSummary = ({ placeholder, selectedCount, totalCount }) => {
  const noun = getSummaryNoun(placeholder);

  if (selectedCount <= 0) return placeholder;
  if (totalCount > 0 && selectedCount >= totalCount) {
    return placeholder;
  }

  const labelNoun = selectedCount === 1 ? singularize(noun) : noun;
  return `${selectedCount} ${labelNoun}`;
};

const MultiSelectOption = (props) => {
  const { isSelected, label } = props;

  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input type="checkbox" checked={isSelected} readOnly tabIndex={-1} />
        <span>{label}</span>
      </div>
    </components.Option>
  );
};

const MultiSelectMenuList = (props) => {
  const { children, selectProps } = props;
  const {
    options = [],
    value,
    onChange,
    getOptionValue,
    allButtonLabel = 'Select all',
    noneButtonLabel = 'Deselect all'
  } = selectProps;

  const selectedList = Array.isArray(value) ? value : [];
  const getValueKey = (option) => {
    if (typeof getOptionValue === 'function') {
      return getOptionValue(option);
    }
    return option?.value;
  };

  const selectedKeys = new Set(selectedList.map(getValueKey));
  const totalOptions = Array.isArray(options) ? options.length : 0;
  const hasSelection = selectedKeys.size > 0;

  const handleToggleSelectAll = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (hasSelection) {
      onChange?.([]);
      return;
    }
    onChange?.(options);
  };

  return (
    <components.MenuList {...props}>
      {children}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f8fafc'
        }}
      >
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleToggleSelectAll}
          disabled={totalOptions === 0}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#2563eb',
            fontSize: '0.75rem',
            fontWeight: 600,
            padding: 0,
            opacity: totalOptions === 0 ? 0.45 : 1
          }}
        >
          {hasSelection ? noneButtonLabel : allButtonLabel}
        </button>
      </div>
    </components.MenuList>
  );
};

const MultiSelectSummaryValue = (props) => {
  const { index, selectProps } = props;
  if (index !== 0) return null;

  const selectedValues = Array.isArray(selectProps?.value) ? selectProps.value : [];
  const totalOptions = Array.isArray(selectProps?.options) ? selectProps.options.length : 0;
  const showDefaultPlaceholder = selectedValues.length === 0 || (totalOptions > 0 && selectedValues.length >= totalOptions);
  const summaryText = buildMultiSelectSummary({
    placeholder: selectProps?.placeholder,
    selectedCount: selectedValues.length,
    totalCount: totalOptions
  });

  return (
    <div style={{
      fontSize: '0.875rem',
      color: showDefaultPlaceholder ? '#9ca3af' : '#1f2937',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%'
    }}>
      {summaryText}
    </div>
  );
};

const DropdownFilter = ({
  icon: Icon,
  value,
  onChange,
  options = [],
  placeholder = "All",
  isClearable = true,
  isDisabled = false,
  style = {},
  selectStyles = {},
  containerStyle = {}, 
  className = "basic-single-select",
  classNamePrefix = "select",
  isMulti = false,
  showMultiSelectActions = true,
  formatOptionLabel = null,
  getOptionLabel = null,
  getOptionValue = null,
  multiSelectAllLabel = 'Select all',
  multiSelectNoneLabel = 'Deselect all'
}) => {
  const defaultSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '48px',
      height: '48px',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderLeft: 'none',
      borderRadius: '0 8px 8px 0',
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid #d1d5db',
        borderLeft: 'none'
      }
    }),
    container: (base) => ({
      ...base,
      width: '100%'
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0.5rem 1rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      flexWrap: isMulti ? 'nowrap' : 'wrap' // Prevent wrapping for multiselect
    }),
    multiValue: (base) => ({
      ...base,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
      flexShrink: 1,
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 0,
      margin: 0,
      padding: 0
    }),
    multiValueLabel: (base) => ({
      ...base,
      padding: 0
    }),
    multiValueRemove: (base) => ({
      ...base,
      cursor: 'pointer',
      flexShrink: 0,
      ':hover': {
        backgroundColor: '#f1f5f9',
        color: '#ef4444'
      }
    }),
    singleValue: (base) => ({
      ...base,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#9ca3af',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }),
    clearIndicator: (base) => ({
      ...base,
      cursor: 'pointer',
      flexShrink: 0,
      ':hover': {
        color: '#ef4444'
      }
    }),
    dropdownIndicator: (base) => ({
      ...base,
      cursor: 'pointer',
      flexShrink: 0,
      ':hover': {
        color: '#374151'
      }
    }),
    indicatorsContainer: (base) => ({
      ...base,
      flexShrink: 0
    }),
    menuPortal: (base) => ({ ...base, zIndex: 20 }),
    menu: (base) => ({ ...base, zIndex: 20 }),
    ...selectStyles
  };

  // Prepare Select props, only including functions if they are actually functions
  const selectProps = {
    value: isMulti ? (Array.isArray(value) ? value : []) : value,
    onChange,
    options,
    placeholder,
    isClearable,
    isDisabled,
    isMulti,
    className,
    classNamePrefix,
    styles: defaultSelectStyles,
    menuPortalTarget: document.body,
    components: isMulti && showMultiSelectActions
      ? {
        Option: MultiSelectOption,
        MenuList: MultiSelectMenuList,
        MultiValue: MultiSelectSummaryValue,
        MultiValueRemove: () => null
      }
      : (isMulti
        ? {
          Option: MultiSelectOption,
          MultiValue: MultiSelectSummaryValue,
          MultiValueRemove: () => null
        }
        : undefined),
    hideSelectedOptions: false,
    closeMenuOnSelect: false,
    allButtonLabel: multiSelectAllLabel,
    noneButtonLabel: multiSelectNoneLabel
  };

  // Only add these props if they are functions
  if (typeof formatOptionLabel === 'function') {
    selectProps.formatOptionLabel = formatOptionLabel;
  }
  if (typeof getOptionLabel === 'function') {
    selectProps.getOptionLabel = getOptionLabel;
  }
  if (typeof getOptionValue === 'function') {
    selectProps.getOptionValue = getOptionValue;
  }

  return (
    <div style={{ width: '100%', maxWidth: '300px', overflow: 'hidden', flexShrink: 0, ...containerStyle }}>
      <InputGroup style={{ 
        position: 'relative', 
        flexShrink: 0, 
        width: '100%', 
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        ...style 
      }}>
        <InputGroup.Text style={{
          border: '1px solid #d1d5db',
          borderRight: 'none',
          borderRadius: '8px 0 0 8px',
          backgroundColor: '#f8fafc',
          color: '#6b7280',
          fontSize: '0.875rem',
          minHeight: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 0.75rem',
          width: '48px',
          flexShrink: 0,
          minWidth: '48px'
        }}>
          {Icon && <Icon style={{ fontSize: '0.875rem' }} />}
        </InputGroup.Text>
        <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
          <Select {...selectProps} />
        </div>
      </InputGroup>
    </div>
  );
};

export default DropdownFilter;